import {
  formatLocalDate,
  type Instant,
  type LocalDate,
  type Money,
  type Rate,
  fromApiMoney,
  parseInstant,
  parseLocalDate,
  rate,
  toApiMoney,
  toLocalDateUtc,
} from '@taler/domain'
import type { OperationBody, OperationQuery, Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'
import { projectQuery, type QueryProjection, queryFingerprint } from '../http/query.ts'
import type { Page } from './page.ts'

export type EntryClass = Schemas['EntryResponse']['entryClass']
export type EntryKind = Schemas['EntryResponse']['kind']
export type EntrySource = Schemas['EntryResponse']['source']
export type AdjustmentKind = Schemas['EntryResponse']['adjustmentKind']

export interface Posting {
  readonly id: string
  readonly accountId: string
  readonly amount: Money
  readonly amountBase: Money
  /** Present when `amount.currency` differs from the ledger's base currency: the rate applied to get `amountBase`. */
  readonly rateToBase: Rate | undefined
  /** In the requested display currency; absent when none was requested or no rate covers the date. */
  readonly displayAmount: Money | undefined
  readonly quantity: number
  readonly memo: string | undefined
  /** The expense account a contact posting's share was carved out of, see AGENTS.md's Money section. */
  readonly splitFromAccountId: string | undefined
}

export interface Entry {
  readonly id: string
  readonly date: LocalDate
  readonly entryClass: EntryClass
  readonly kind: EntryKind | undefined
  readonly source: EntrySource
  /** Set on a system adjustment; `FX_REVALUATION` is the month-end job's own, system-managed entry. */
  readonly adjustmentKind: AdjustmentKind | undefined
  readonly memo: string
  readonly notes: string | undefined
  readonly payee: string | undefined
  readonly merchantId: string | undefined
  readonly tags: readonly string[]
  readonly total: Money
  /** In the requested display currency; absent when none was requested or no rate covers the date. */
  readonly displayTotal: Money | undefined
  /** True when a display currency was requested and no rate covers this entry's date for some figure: that figure is native. */
  readonly displayRateMissing: boolean
  readonly postings: readonly Posting[]
  readonly reversalOfEntryId: string | undefined
  readonly externalId: string | undefined
  readonly createdAt: Instant
  readonly createdBy: string
  readonly updatedAt: Instant
}

export interface PostingLineInput {
  readonly accountId: string
  readonly amount: Money
  readonly memo?: string
  readonly quantity?: number
  /** The expense account a contact posting's share was carved out of, see AGENTS.md's Money section. */
  readonly splitFromAccountId?: string
}

export interface PostEntryInput {
  readonly date: LocalDate
  readonly postings: readonly PostingLineInput[]
  readonly memo?: string
  readonly payee?: string
  readonly notes?: string
  readonly tags?: readonly string[]
  readonly externalId?: string
}

export type EntryListQuery = OperationQuery<'entries_list'>

const ENTRY_LIST_PROJECTION: QueryProjection<EntryListQuery> = {
  limit: (value) => String(value),
  cursor: (value) => value,
  from: (value) => value,
  until: (value) => value,
  accountId: (value) => value,
  includeDescendants: (value) => String(value),
  merchantId: (value) => value,
  kind: (value) => value,
  source: (value) => value,
  minAmount: (value) => value,
  maxAmount: (value) => value,
  search: (value) => value,
  tags: (value) => value,
  displayCurrency: (value) => value,
}

export const entryKeys = {
  list: (ledgerId: string, query: EntryListQuery): CacheKey =>
    ledgerKey(ledgerId, 'entries', 'list', queryFingerprint(projectQuery(query, ENTRY_LIST_PROJECTION))),
  detail: (ledgerId: string, entryId: string, displayCurrency?: string): CacheKey =>
    ledgerKey(ledgerId, 'entries', 'detail', entryId, displayCurrency ?? 'native'),
  /** Everything a write to the journal makes stale. */
  invalidatedByWrite: (ledgerId: string): Invalidations => [ledgerKey(ledgerId, 'entries')],
} as const

export interface PostingInput {
  readonly accountId: string
  readonly amount: Money
  readonly memo?: string
}

export interface ReplaceEntryInput {
  readonly date: LocalDate
  readonly memo: string
  readonly payee?: string
  readonly notes?: string
  readonly externalId?: string
  readonly postings: readonly PostingInput[]
  readonly tags: readonly string[]
}

export interface EntriesRepository {
  list(ledgerId: string, query?: EntryListQuery, signal?: AbortSignal): Promise<Page<Entry>>
  get(ledgerId: string, entryId: string, displayCurrency?: string, signal?: AbortSignal): Promise<Entry>
  create(ledgerId: string, input: PostEntryInput): Promise<Entry>
  /** One idempotency key for the whole array - see `createTalerClient`'s per-payload fingerprint. */
  batchCreate(ledgerId: string, inputs: readonly PostEntryInput[]): Promise<readonly Entry[]>
  replace(ledgerId: string, entryId: string, input: ReplaceEntryInput): Promise<Entry>
  /** A reversal is a new entry linked back via `reversalOfEntryId`; the original is untouched. */
  reverse(ledgerId: string, entryId: string, date?: LocalDate): Promise<Entry>
  delete(ledgerId: string, entryId: string): Promise<void>
}

export function createEntriesRepository(client: TalerClient): EntriesRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/entries`
  const one = (ledgerId: string, entryId: string) => `${base(ledgerId)}/${encodeURIComponent(entryId)}`

  async function fetchPage(
    ledgerId: string,
    query: EntryListQuery,
    signal?: AbortSignal,
  ): Promise<Page<Entry>> {
    const page = await client.call<Schemas['PageResponseEntryResponse']>({
      method: 'GET',
      path: base(ledgerId),
      query: projectQuery(query, ENTRY_LIST_PROJECTION),
      ...(signal === undefined ? {} : { signal }),
    })
    return { items: page.items.map(toEntry), nextCursor: page.nextCursor }
  }

  return {
    async list(ledgerId, query = {}, signal) {
      return fetchPage(ledgerId, query, signal)
    },

    async get(ledgerId, entryId, displayCurrency, signal) {
      return toEntry(
        await client.call<Schemas['EntryResponse']>({
          method: 'GET',
          path: one(ledgerId, entryId),
          ...(displayCurrency === undefined ? {} : { query: { displayCurrency } }),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async create(ledgerId, input) {
      const body = toPostEntryRequest(input)
      const entry = toEntry(
        await client.call<Schemas['EntryResponse'], Schemas['PostEntryRequest']>({
          method: 'POST',
          path: base(ledgerId),
          body,
        }),
      )
      // A posted entry is a new act each time, so an identical payload must not replay this
      // response. Only a retry of a failed attempt should reuse the key.
      client.idempotencyKeys.forget({ method: 'POST', path: base(ledgerId), query: null, body })
      return entry
    },

    async batchCreate(ledgerId, inputs) {
      const path = `${base(ledgerId)}/batch`
      const body = { entries: inputs.map(toPostEntryRequest) }
      const entries = await client.call<Schemas['EntryResponse'][], Schemas['BatchPostEntryRequest']>({
        method: 'POST',
        path,
        body,
      })
      client.idempotencyKeys.forget({ method: 'POST', path, query: null, body })
      return entries.map(toEntry)
    },

    async replace(ledgerId, entryId, input) {
      return toEntry(
        await client.call<Schemas['EntryResponse'], OperationBody<'entries_replace'>>({
          method: 'PUT',
          path: one(ledgerId, entryId),
          body: {
            date: formatLocalDate(input.date),
            memo: input.memo,
            postings: input.postings.map(toReplacePostingLineRequest),
            tags: [...input.tags],
            ...(input.payee === undefined ? {} : { payee: input.payee }),
            ...(input.notes === undefined ? {} : { notes: input.notes }),
            ...(input.externalId === undefined ? {} : { externalId: input.externalId }),
          },
        }),
      )
    },

    async reverse(ledgerId, entryId, date) {
      return toEntry(
        await client.call<Schemas['EntryResponse'], OperationBody<'entries_reverse'>>({
          method: 'POST',
          path: `${one(ledgerId, entryId)}/reverse`,
          body: { ...(date === undefined ? {} : { date: formatLocalDate(date) }) },
        }),
      )
    },

    async delete(ledgerId, entryId) {
      await client.call<undefined>({ method: 'DELETE', path: one(ledgerId, entryId) })
    },
  }
}

function toPostingLineRequest(input: PostingLineInput): Schemas['PostingLineRequest'] {
  return {
    accountId: input.accountId,
    // Money.currency is already a validated CurrencyCode; the generated schema type
    // just spells the same ISO-4217 set as its own literal union.
    amount: toApiMoney(input.amount) as Schemas['PostingLineRequest']['amount'],
    ...(input.memo === undefined ? {} : { memo: input.memo }),
    ...(input.quantity === undefined ? {} : { quantity: input.quantity }),
    ...(input.splitFromAccountId === undefined ? {} : { splitFromAccountId: input.splitFromAccountId }),
  }
}

function toPostEntryRequest(input: PostEntryInput): Schemas['PostEntryRequest'] {
  return {
    date: formatLocalDate(input.date),
    postings: input.postings.map(toPostingLineRequest),
    tags: [...(input.tags ?? [])],
    ...(input.memo === undefined ? {} : { memo: input.memo }),
    ...(input.payee === undefined ? {} : { payee: input.payee }),
    ...(input.notes === undefined ? {} : { notes: input.notes }),
    ...(input.externalId === undefined ? {} : { externalId: input.externalId }),
  }
}

function toReplacePostingLineRequest(input: PostingInput): Schemas['PostingLineRequest'] {
  return {
    accountId: input.accountId,
    // ApiMoney.currency is deliberately a plain string (domain stays decoupled from the
    // generated currency enum); Money's own currency is already a validated CurrencyCode.
    amount: toApiMoney(input.amount) as Schemas['PostingLineRequest']['amount'],
    ...(input.memo === undefined ? {} : { memo: input.memo }),
  }
}

function toPosting(response: Schemas['PostingResponse']): Posting {
  const amount = fromApiMoney(response.amount)
  const amountBase = fromApiMoney(response.amountBase)
  return {
    id: response.id,
    accountId: response.accountId,
    amount,
    amountBase,
    rateToBase:
      response.nativeToBaseRate === undefined
        ? undefined
        : rate(response.nativeToBaseRate, amount.currency, amountBase.currency),
    displayAmount: response.displayAmount === undefined ? undefined : fromApiMoney(response.displayAmount),
    quantity: response.quantity,
    memo: response.memo,
    splitFromAccountId: response.splitFromAccountId,
  }
}

/** A timed entry date belongs to its UTC calendar day. */
function entryDay(value: string): LocalDate {
  return value.includes('T') ? toLocalDateUtc(parseInstant(value)) : parseLocalDate(value)
}

function toEntry(response: Schemas['EntryResponse']): Entry {
  return {
    id: response.id,
    date: entryDay(response.date),
    entryClass: response.entryClass,
    kind: response.kind,
    source: response.source,
    adjustmentKind: response.adjustmentKind,
    memo: response.memo,
    notes: response.notes,
    payee: response.payee,
    merchantId: response.merchantId,
    tags: response.tags,
    total: fromApiMoney(response.total),
    displayTotal: response.displayTotal === undefined ? undefined : fromApiMoney(response.displayTotal),
    displayRateMissing: response.displayRateMissing === true,
    postings: response.postings.map(toPosting),
    reversalOfEntryId: response.reversalOfEntryId,
    externalId: response.externalId,
    createdAt: parseInstant(response.createdAt),
    createdBy: response.createdBy,
    updatedAt: parseInstant(response.updatedAt),
  }
}
